import React, { useContext, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import Box from '@mui/material/Box';

import { DateTimeFormats } from '../../common/constants/dateFormats';
import { ApplicationLifecycle } from '../../common/constants/lifecycle';
import { calculateSla, getFormattedTimestamp } from '../../common/helpers';
import { HttpService } from '../../common/http.service';
import { IFilter } from '../../common/interfaces/IFilter';
import { IKeyValMap } from '../../common/interfaces/IKeyValMap';
import { QuickFilterType } from '../../common/interfaces/quickFilters/QuickFilters';
import { TaskQueryRequest } from '../../common/interfaces/quickFilters/TaskQueryRequest';
import { AuthContext } from '../login/AuthWrapper';
import { useSelector } from './FilterBar';
import { PaginatedTable } from './PaginatedTable';
import { taskViewActions } from './slices/statusSlice';
import { TaskListFilters } from './TaskListFilters';

interface TaskListRow {
  applicationId: number;
  name: string;
  carrierPolicyId: string;
  lastDecision: string;
  stage: string;
  sla: string;
  createdAt: string;
  product: string;
  underwriterName: string;
  casemanagerName: string;
}

const columns: any[] = [
  { columnName: 'name', display: 'Name' },
  { columnName: 'carrierPolicyId', display: 'PolicyID' },
  { columnName: 'lastDecision', display: 'Decision' },
  { columnName: 'stage', display: 'Stage' },
  { columnName: 'sla', display: 'SLA' },
  { columnName: 'createdAt', display: 'Created at' },
  { columnName: 'product', display: 'Product' },
  { columnName: 'underwriterName', display: 'Underwriter' },
  { columnName: 'casemanagerName', display: 'Casemanager' },
];

export const TaskList = ({
  quickFilterType,
  taskQuery,
}: {
  quickFilterType: string;
  taskQuery: any;
}) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const auth = useContext(AuthContext);

  const [taskRows, setTaskRows] = React.useState<any[]>([]);
  const [totalCount, setTotalCount] = React.useState(0);
  const page = useSelector(state => state.taskView.page);
  const [carrierFilter, setCarrierFilter] = React.useState([] as IFilter[]);
  const [typeFilter, setTypeFilter] = React.useState([] as IFilter[]);

  const handleRowClick = (row: TaskListRow) => {
    navigate('/workbench/applications/' + row.applicationId);
  };

  const handleChangePage = (_event: any, newPage: number) => {
    dispatch(
      taskViewActions.onPageChange({
        page: newPage,
      }),
    );
  };
  const formatTime = (time: string): string => {
    return (
      getFormattedTimestamp(time, DateTimeFormats.SIMPLE_DATE) +
      ' ' +
      getFormattedTimestamp(time, DateTimeFormats.TIME_AM_PM)
    );
  };
  const formatApplicationStage = (stage: string): string => {
    const replacedUnderscore = stage.replace(new RegExp('_', 'g'), ' ');
    return (
      replacedUnderscore.charAt(0).toUpperCase() +
      replacedUnderscore.slice(1).toLowerCase()
    );
  };

  useEffect(() => {
    (async () => {
      if (quickFilterType === QuickFilterType.REFERRAL_VIEW) {
        const pageAndLimit = `limit=10&page=${page}`;
        const lifeCycle = `lifecycleFilter[]=PIA`;
        const referralQuery = `is_referral_case=${taskQuery.isReferralCase}&null_referral=${taskQuery.nullReferral}`;
        const sortByReferral = `sort_by=referralUpdatedAt&sort_order=DESC`;
        const queryParams = [
          pageAndLimit,
          lifeCycle,
          referralQuery,
          sortByReferral,
        ];
        if (taskQuery.filterByCurrentUser) {
          const filterByUser = `filter_user[]=${auth.id}`;
          queryParams.push(filterByUser);
        }
        if (taskQuery.filterReferralByCurrentUser) {
          const filterByUser = `referral_user_id=${auth.id}`;
          queryParams.push(filterByUser);
        }

        const getApplicationsPath = `applications?${queryParams.join('&')}`;
        const applicationData: IKeyValMap =
          await HttpService.getInstance().get(getApplicationsPath);
        if (!applicationData?.parsedBody) {
          setTotalCount(0);
          return;
        }

        const applicationRows: TaskListRow[] =
          applicationData?.parsedBody?.items.map((item: any) => {
            const row: TaskListRow = {
              applicationId: item.id,
              name: item.customerDisplayName,
              carrierPolicyId: item.carrierPolicyNumber,
              lastDecision: item.lastDecisionType,
              stage: item.stage ? formatApplicationStage(item.stage) : '',
              sla: item.sla ? calculateSla(new Date(item.sla)) + ' hour' : '',
              createdAt: formatTime(item.creationDate),
              product: item.carrier + ' ' + item.product,
              underwriterName: item.underwriterDisplayName,
              casemanagerName: item.caseManagerDisplayName,
            };
            return row;
          });
        if (applicationRows) setTaskRows(applicationRows);
        setTotalCount(applicationData?.parsedBody?.totalItems ?? 0);
        return;
      }
      const path = 'task/fetchApplications';

      // Map dropdown options (RUW/PIA) with multiple lifecycle items.
      const lifeCycleFilter = new Set<string>();
      if (typeFilter.find(f => f.value === 'PIA')) {
        lifeCycleFilter.add(ApplicationLifecycle.PIA);
        lifeCycleFilter.add(ApplicationLifecycle.COMPLETED);
      }
      if (typeFilter.find(f => f.value === 'RUW')) {
        lifeCycleFilter.add(ApplicationLifecycle.PRE_DECISION);
        lifeCycleFilter.add(ApplicationLifecycle.POST_DECISION);
        lifeCycleFilter.add(ApplicationLifecycle.COMPLETED);
      }

      const body: TaskQueryRequest = {
        ...taskQuery,
        limit: 10,
        page,
        carrier: carrierFilter.map(({ value }) => value),
        lifeCycle: [...lifeCycleFilter.values()],
      };
      const data: IKeyValMap = await HttpService.getInstance().post(path, body);
      if (!data?.parsedBody) {
        setTotalCount(0);
        return;
      }

      const rows: TaskListRow[] = data?.parsedBody?.items.map((item: any) => {
        const row: TaskListRow = {
          applicationId: item.id,
          name: item.customerDisplayName,
          carrierPolicyId: item.carrierPolicyNumber,
          lastDecision: item.lastDecisionType,
          stage: item.stage ? formatApplicationStage(item.stage) : '',
          sla: item.sla ? calculateSla(new Date(item.sla)) + ' hour' : '',
          createdAt: formatTime(item.creationDate),
          product: item.carrier + ' ' + item.product,
          underwriterName: item.underwriterDisplayName,
          casemanagerName: item.caseManagerDisplayName,
        };
        return row;
      });
      if (rows) setTaskRows(rows);
      setTotalCount(data?.parsedBody?.totalItems ?? 0);
    })();
  }, [page, taskQuery, carrierFilter, typeFilter]);

  useEffect(() => {
    // Reset filters when selected task changes
    if (carrierFilter?.length > 0 || typeFilter?.length > 0) {
      setCarrierFilter([]);
      setTypeFilter([]);
    }
  }, [taskQuery]);

  return (
    <>
      {quickFilterType === QuickFilterType.TASK_LIST_VIEW && (
        <Box mb={2}>
          <TaskListFilters
            carrierFilter={carrierFilter}
            setCarrierFilter={setCarrierFilter}
            typeFilter={typeFilter}
            setTypeFilter={setTypeFilter}
          />
        </Box>
      )}
      <PaginatedTable
        headers={columns}
        rowValues={taskRows}
        handleRowClick={handleRowClick}
        currentPage={page}
        totalCount={totalCount}
        handlePageChange={handleChangePage}
      />
    </>
  );
};
